import type { ProfileConfig } from "@worker/config-schema";
import { renderClassicTopLeft } from "./templates/classic-topleft";
import { renderCenteredStack } from "./templates/centered-stack";
import { renderCompactInline } from "./templates/compact-inline";
import { renderCluster } from "./templates/cluster";
import { renderSplit } from "./templates/split";
import { renderHero } from "./templates/hero";
import { renderRetroTerminal } from "./templates/retro-terminal";
import { renderMagazine } from "./templates/magazine";
import { renderWindowOs } from "./templates/window-os";
import { renderTradingCard } from "./templates/trading-card";
import { attachTilt } from "./effects/tilt";
import { mountGate } from "./components/gate";
import { mountBackground } from "./effects/background";
import { attachCursorTrail } from "./effects/cursor-trail";
import { applyUsernameEffect } from "./effects/username-effects";
import { animateTitle } from "./effects/title-animation";
import { resolveAsset } from "./lib/assets";
import { installTooltips } from "./lib/tooltip";
import { startDiscordSync } from "./lib/discord-sync";
import { mountAdminLinkIfAllowed } from "./components/admin-link";

/**
 * Listen for the Lanyard widget's `bio:discord-profile` event and swap
 * the tab favicon to the user's Discord avatar (static, `.webp` — never
 * `.gif`, because animated favicons are browser-inconsistent and noisy).
 * Returns a cleanup that detaches the listener; the original favicon
 * href is restored so admin previews and teardown don't leave a stale
 * Discord icon behind.
 */
function syncFaviconFromDiscord(): () => void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return () => {};
  const original = link.href;
  const listener = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | { avatarUrl?: string | null }
      | undefined;
    if (!detail?.avatarUrl) return;
    // Force static extension. Lanyard's widget URL uses `.gif` when the
    // user's Discord avatar is animated (Nitro); `.webp` is always
    // available as the static fallback.
    const staticUrl = detail.avatarUrl.replace(/\.gif(\?|$)/, ".webp$1");
    if (link.href !== staticUrl) link.href = staticUrl;
  };
  document.addEventListener("bio:discord-profile", listener);
  return () => {
    document.removeEventListener("bio:discord-profile", listener);
    if (original && link.href !== original) link.href = original;
  };
}

/**
 * Hydrate config from the Worker API. Static-first architecture: the HTML
 * is served as-is (by Pages in prod or the Worker's passthrough in dev),
 * and the client asks `/bio-api/config` for the live data.
 *
 * Legacy path: older builds injected the config as a `<script id="site-config">`
 * into <head>. We still honor that if present so admin preview postMessage
 * loads work without an extra network round-trip.
 */
function readInjectedConfig(): ProfileConfig | null {
  const el = document.getElementById("site-config");
  if (!el || el.textContent == null) return null;
  try {
    return JSON.parse(el.textContent) as ProfileConfig;
  } catch (err) {
    console.error("[cfg] failed to parse injected config", err);
    return null;
  }
}

async function fetchConfig(
  retries = 2,
  backoffMs = 400,
): Promise<ProfileConfig | null> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const r = await fetch("/bio-api/config", { cache: "no-store" });
      if (!r.ok) throw new Error(`status ${r.status}`);
      return (await r.json()) as ProfileConfig;
    } catch (err) {
      if (attempt === retries) {
        console.error("[cfg] failed to fetch /bio-api/config", err);
        return null;
      }
      // linear backoff — keeps the UX snappy if the worker is just cold
      await new Promise((resolve) => setTimeout(resolve, backoffMs * (attempt + 1)));
    }
  }
  return null;
}

function pickTemplate(
  config: ProfileConfig,
): (c: ProfileConfig, opts: { gateOpened: Promise<void> }) => HTMLElement {
  switch (config.template) {
    case "centered-stack":    return renderCenteredStack;
    case "compact-inline":    return renderCompactInline;
    case "cluster":           return renderCluster;
    case "split":             return renderSplit;
    case "hero":              return renderHero;
    case "retro-terminal":    return renderRetroTerminal;
    case "magazine":          return renderMagazine;
    case "window-os":         return renderWindowOs;
    case "trading-card":      return renderTradingCard;
    case "classic-topleft":
    default:                  return renderClassicTopLeft;
  }
}

async function boot() {
  installTooltips();
  const isPreview = new URLSearchParams(window.location.search).get("preview") === "1";
  // Flag the body for preview mode so CSS can suppress entrance animations
  // that would otherwise re-fire on every admin config edit.
  if (isPreview) document.body.setAttribute("data-preview", "1");
  // Admin-gear button is for the public view; suppress it inside the admin
  // preview iframe so it doesn't sit on top of the preview.
  if (!isPreview) {
    void mountAdminLinkIfAllowed();
  }
  // Admin preview: swap config when the parent posts a draft.
  if (isPreview) {
    installPreviewBridge();
  }
  const app = document.getElementById("app");
  if (!app) {
    console.warn("[boot] #app not found");
    return;
  }
  app.innerHTML = "";

  const config = readInjectedConfig() ?? (await fetchConfig());
  if (!config) {
    app.innerHTML = `<p class="boot error">config unavailable — hydrate failed</p>`;
    return;
  }

  // Replace the placeholder <title> with the configured site title and
  // keep the <meta description> in sync. Crawlers get the static values
  // baked into index.html; live users see the config-driven ones.
  if (config.meta?.title) {
    activeCleanups.push(
      animateTitle(config.meta.title, config.meta.titleAnimation ?? "none"),
    );
  }
  if (config.meta?.description) {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (meta) meta.content = config.meta.description;
  }
  // If the config provides an og image or favicon, use it for the tab icon.
  // `faviconFromDiscord` layers on top of this: it registers a listener
  // that swaps the href to the Discord avatar once Lanyard emits, so the
  // config-provided icon is used as the pre-Lanyard fallback.
  const favAsset = config.meta?.favicon ?? config.meta?.ogImage;
  if (favAsset) {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) link.href = resolveAsset(favAsset);
  }
  if (config.meta?.faviconFromDiscord) {
    activeCleanups.push(syncFaviconFromDiscord());
  }
  // Standalone Discord sync: fetch Lanyard data + broadcast to listeners
  // (avatar sync, favicon sync) independently of whether the on-card
  // Lanyard widget is mounted. If the widget is ALSO mounted for the
  // same user, both broadcast and listeners dedupe by value match.
  if (config.discord?.userId) {
    activeCleanups.push(startDiscordSync(config.discord.userId));
  }

  // Background layer sits behind everything (including the gate) so the
  // gate's backdrop-filter can frost over it during the click-to-enter.
  const bgLayer = mountBackground(config.background, config.motion);
  document.body.prepend(bgLayer);

  // Custom cursor image (if any) via CSS variable. Hotspot (the pixel
  // that registers clicks) is set to the image's center — we probe the
  // image to measure its natural dimensions, then write
  // --cursor-hotspot-x/y as integers for the CSS `cursor: url() X Y` syntax.
  if (config.cursor.image) {
    void applyCustomCursor(resolveAsset(config.cursor.image));
  }
  activeCleanups.push(attachCursorTrail(config.cursor.particleTrail, config.motion));

  // Propagate surface-style toggles to CSS as data-* attributes on <body>,
  // so every widget/link/social picks them up without prop-drilling.
  applySurfaceAttrs(config.surfaces);
  applySizing(config.sizing);
  document.documentElement.style.setProperty(
    "--global-scale",
    String(config.globalScale ?? 1),
  );

  // Build the gate first so the card can receive its opened-promise (needed
  // for audio autoplay and any other reveal-triggered effects).
  const { element: gate, opened } = mountGate(config.gate, config.motion);

  const render = pickTemplate(config);
  const card = render(config, { gateOpened: opened });
  app.append(card);
  activeCleanups.push(attachTilt(card, config.card.tilt, config.motion));

  const usernameEl = card.querySelector<HTMLElement>(".username");
  if (usernameEl) {
    activeCleanups.push(applyUsernameEffect(usernameEl, config.profile, config.motion));
  }

  if (gate.classList.contains("gate--disabled")) {
    document.body.classList.add("is-ready");
  } else {
    document.body.classList.add("is-gated");
    document.body.append(gate);
    opened.then(() => {
      document.body.classList.remove("is-gated");
      document.body.classList.add("is-ready");
    });
  }
}

boot();

function applySurfaceAttrs(surfaces: ProfileConfig["surfaces"]): void {
  const body = document.body;
  for (const [name, style] of Object.entries(surfaces)) {
    body.setAttribute(`data-surface-${name}-bg`, style.backgroundEnabled ? "on" : "off");
    body.setAttribute(`data-surface-${name}-border`, style.borderEnabled ? "on" : "off");
    body.setAttribute(`data-surface-${name}-glow`, style.glowEnabled ? "on" : "off");
    // Project the per-surface color/intensity values into CSS custom
    // properties scoped to <body>. Each surface's CSS reads them via
    // `var(--surface-<name>-<prop>)` so the admin can tint independently.
    body.style.setProperty(`--surface-${name}-bg-color`, style.backgroundColor);
    body.style.setProperty(`--surface-${name}-bg-alpha`, String(style.backgroundOpacity));
    body.style.setProperty(`--surface-${name}-blur`, `${style.blurPx}px`);
    body.style.setProperty(`--surface-${name}-border-color`, style.borderColor);
    body.style.setProperty(`--surface-${name}-border-width`, `${style.borderWidthPx}px`);
    body.style.setProperty(`--surface-${name}-border-style`, style.borderStyle);
    body.style.setProperty(`--surface-${name}-glow-color`, style.glowColor);
    body.style.setProperty(`--surface-${name}-glow-intensity`, String(style.glowIntensity));
  }
}

/**
 * Apply a custom cursor image with hotspot at the image's CENTER.
 *
 * Browsers render cursor images at their source pixel size, so a 256x256
 * upload looks comically huge next to the OS's 16-24px default. We
 * down-sample onto a 32x32 canvas first, then feed the resulting data
 * URL to CSS. Aspect ratio preserved — portrait/landscape images are
 * centered in the 32x32 box with transparent padding.
 *
 * Hotspot is the center of the rendered cursor (16, 16).
 *
 * Failed decode (404, CORS taint) silently falls back to `cursor: auto`.
 */
async function applyCustomCursor(src: string): Promise<void> {
  const TARGET = 32;
  const img = new Image();
  img.crossOrigin = "anonymous"; // permit toDataURL on cross-origin responses that send CORS headers
  img.src = src;
  try {
    await img.decode();
  } catch {
    return;
  }
  const srcW = img.naturalWidth || TARGET;
  const srcH = img.naturalHeight || TARGET;
  const scale = Math.min(TARGET / srcW, TARGET / srcH);
  const drawW = Math.max(1, Math.round(srcW * scale));
  const drawH = Math.max(1, Math.round(srcH * scale));
  const dx = Math.round((TARGET - drawW) / 2);
  const dy = Math.round((TARGET - drawH) / 2);

  const canvas = document.createElement("canvas");
  canvas.width = TARGET;
  canvas.height = TARGET;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  try {
    ctx.drawImage(img, dx, dy, drawW, drawH);
  } catch {
    return; // tainted canvas; skip custom cursor
  }
  let dataUrl: string;
  try {
    dataUrl = canvas.toDataURL("image/png");
  } catch {
    return;
  }

  const r = document.documentElement;
  r.style.setProperty("--cursor-image", `url(${JSON.stringify(dataUrl)})`);
  r.style.setProperty("--cursor-hotspot-x", String(TARGET / 2));
  r.style.setProperty("--cursor-hotspot-y", String(TARGET / 2));
  r.classList.add("has-custom-cursor");
}

function applySizing(sizing: ProfileConfig["sizing"]): void {
  const r = document.documentElement;
  r.style.setProperty("--social-icon-size", `${sizing.socialIconPx}px`);
  r.style.setProperty("--link-icon-size", `${sizing.linkIconPx}px`);
  r.style.setProperty("--badge-size", `${sizing.badgePx}px`);
  r.style.setProperty("--badge-gap", `${sizing.badgeGapPx}px`);
  // Single-color overlay for all badge icons via filter. Empty string clears.
  r.style.setProperty(
    "--badge-tint-filter",
    sizing.badgeTint
      ? `drop-shadow(0 0 0 ${sizing.badgeTint}) saturate(0) brightness(100) opacity(0)`
      : "none",
  );
  if (sizing.badgeTint) {
    r.style.setProperty("--badge-tint-color", sizing.badgeTint);
  } else {
    r.style.removeProperty("--badge-tint-color");
  }
}

/**
 * Admin preview bridge. When the profile is rendered inside the admin's
 * iframe (`/?preview=1`), the admin posts draft configs over postMessage;
 * we re-run the boot flow against the draft to give live preview with no
 * network round-trip. Only accepts messages from our own origin.
 *
 * UX nuance: re-mounting the gate on every config change would flash the
 * click-to-enter screen each keystroke. We suppress the gate unless the
 * gate config itself has changed since the last preview, so editing (say)
 * the username doesn't pop the gate back up. Initial load in preview mode
 * also skips the gate to save the admin an extra click.
 */
function installPreviewBridge() {
  let lastConfig: ProfileConfig | null = null;
  let lastMsgId = 0;
  window.addEventListener("message", (ev) => {
    if (ev.origin !== window.location.origin) return;
    const data = ev.data as
      | { type?: string; config?: ProfileConfig; msgId?: number }
      | null;
    if (!data || data.type !== "bio:preview-config" || !data.config) return;
    // Drop stale messages that arrive out of order after a newer one.
    // The admin posts with an incrementing msgId; anything lower than the
    // highest we've seen is already obsolete.
    if (typeof data.msgId === "number") {
      if (data.msgId < lastMsgId) return;
      lastMsgId = data.msgId;
    }
    const next = data.config;
    const gateChanged =
      lastConfig == null ||
      JSON.stringify(lastConfig.gate) !== JSON.stringify(next.gate);
    const showGate = lastConfig != null && gateChanged;
    renderFromConfig(next, { showGate });
    lastConfig = next;
  });
}

/**
 * Cleanup functions from the previous render. `attachTilt`, `attachCursor
 * Trail`, and `applyUsernameEffect` each return a dispose fn that removes
 * their event listeners / cancels their animation frames. Without calling
 * them, each admin edit accumulates N stale listeners that keep firing on
 * pointer move (and compounding degrades performance + causes ghost tilt).
 */
let activeCleanups: Array<() => void> = [];

function runCleanups() {
  for (const fn of activeCleanups) {
    try { fn(); } catch { /* best-effort */ }
  }
  activeCleanups = [];
}

function renderFromConfig(config: ProfileConfig, opts: { showGate: boolean }) {
  // Tear down per-component resources BEFORE removing the DOM nodes.
  // Without this, the previous audio player keeps playing while the new one
  // mounts — you get the "double playing" bug after every edit.
  runCleanups();
  document
    .querySelectorAll(
      ".audio-player, .description, .widget, .username, .cursor-trail, .bio-tearable",
    )
    .forEach((n) => n.dispatchEvent(new Event("bio:teardown")));
  document.body.classList.remove("is-gated", "is-ready");
  document
    .querySelectorAll(".bg, .gate, .cursor-trail, .admin-link")
    .forEach((n) => n.remove());

  const app = document.getElementById("app");
  if (!app) return;
  app.innerHTML = "";
  void bootWith(config, opts);
}

async function bootWith(config: ProfileConfig, opts: { showGate: boolean }) {
  const app = document.getElementById("app");
  if (!app) return;
  // Mirror boot()'s title + favicon handling so admin previews reflect
  // changes to those fields without a full page reload.
  if (config.meta?.title) {
    activeCleanups.push(
      animateTitle(config.meta.title, config.meta.titleAnimation ?? "none"),
    );
  }
  if (config.meta?.faviconFromDiscord) {
    activeCleanups.push(syncFaviconFromDiscord());
  }
  if (config.discord?.userId) {
    activeCleanups.push(startDiscordSync(config.discord.userId));
  }
  const bgLayer = mountBackground(config.background, config.motion);
  document.body.prepend(bgLayer);
  if (config.cursor.image) {
    void applyCustomCursor(resolveAsset(config.cursor.image));
  } else {
    document.documentElement.classList.remove("has-custom-cursor");
    document.documentElement.style.removeProperty("--cursor-image");
    document.documentElement.style.removeProperty("--cursor-hotspot-x");
    document.documentElement.style.removeProperty("--cursor-hotspot-y");
  }
  const trailCleanup = attachCursorTrail(config.cursor.particleTrail, config.motion);
  activeCleanups.push(trailCleanup);
  applySurfaceAttrs(config.surfaces);
  applySizing(config.sizing);
  document.documentElement.style.setProperty(
    "--global-scale",
    String(config.globalScale ?? 1),
  );

  // Never autofocus the gate inside the preview iframe — it steals focus
  // from the admin panel's text inputs on every postMessage re-render.
  const { element: gate, opened } = mountGate(config.gate, config.motion, { autofocus: false });
  const render = pickTemplate(config);
  const card = render(config, { gateOpened: opened });
  app.append(card);
  const tiltCleanup = attachTilt(card, config.card.tilt, config.motion);
  activeCleanups.push(tiltCleanup);
  const usernameEl = card.querySelector<HTMLElement>(".username");
  if (usernameEl) {
    const usernameCleanup = applyUsernameEffect(usernameEl, config.profile, config.motion);
    activeCleanups.push(usernameCleanup);
  }

  const gateDisabled = gate.classList.contains("gate--disabled");
  if (gateDisabled || !opts.showGate) {
    // Skip the gate — either it's disabled in config, or we're in preview
    // mode and the gate config hasn't changed.
    document.body.classList.add("is-ready");
  } else {
    document.body.classList.add("is-gated");
    document.body.append(gate);
    opened.then(() => {
      document.body.classList.remove("is-gated");
      document.body.classList.add("is-ready");
    });
  }
}
